import { FormEvent, useEffect, useState } from 'react';
import { ApiError, apiClient } from '../../lib/api';
import { Button } from '../../components/Button';
import { PageStage } from '../../components/PageStage';
import { SectionHead } from '../../components/SectionHead';
import type { JournalEntry, JournalKind } from '../../lib/types';
import { toast } from '../../lib/toast';
import styles from './Journal.module.css';

const KIND_LABELS: Record<JournalKind, string> = {
  morning_brief: 'Утро',
  evening_reflection: 'Вечер',
  freeform: 'Свободно',
};

export function JournalPage() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [kind, setKind] = useState<JournalKind>('morning_brief');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get<{ items: JournalEntry[] } | JournalEntry[]>(
          '/api/v1/journal/entries',
        );
        const list = Array.isArray(res) ? res : (res?.items ?? []);
        setEntries(list);
      } catch (err) {
        if (err instanceof ApiError && err.status >= 500) {
          toast.error('Не удалось загрузить дневник.');
        }
      }
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const entry = await apiClient.post<JournalEntry>('/api/v1/journal/entries', {
        kind,
        content: content.trim(),
        date: new Date().toISOString().slice(0, 10),
      });
      setEntries((prev) => [entry, ...prev]);
      setContent('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось записать.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageStage>
      <SectionHead
        eyebrow="Дневник"
        title="Утро"
        italic="и вечер"
        sub="Короткие заметки. Шифруются. Используются для тёплого утреннего брифинга."
      />

      <form className={styles.form} onSubmit={submit}>
        <div className={styles.kindRow}>
          {(Object.keys(KIND_LABELS) as JournalKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`${styles.kindBtn} ${kind === k ? styles.kindActive : ''}`}
            >
              {KIND_LABELS[k]}
            </button>
          ))}
        </div>
        <textarea
          className={styles.area}
          rows={6}
          value={content}
          onChange={(e) => setContent(e.currentTarget.value)}
          placeholder={
            kind === 'morning_brief'
              ? 'Что вы хотите от сегодняшнего дня? Кого важно увидеть?'
              : kind === 'evening_reflection'
                ? 'Что состоялось? Где ушла энергия? Что назавтра?'
                : 'Свободная запись.'
          }
        />
        <div className={styles.actions}>
          <Button type="submit" disabled={submitting || !content.trim()} trailing="→">
            {submitting ? 'сохраняем' : 'сохранить'}
          </Button>
        </div>
      </form>

      <ol className={styles.list}>
        {entries.map((e) => (
          <li key={e.id} className={styles.entry}>
            <div className={styles.entryHead}>
              <span className={styles.entryKind}>{KIND_LABELS[e.kind]}</span>
              <span className={styles.entryDate}>{e.date}</span>
            </div>
            <p className={styles.entryBody}>{e.content}</p>
          </li>
        ))}
      </ol>
    </PageStage>
  );
}
